import Group from "~/components/institutional/Group"
import Breadcrumb from "~/components/ui/Breadcrumb"
import Icon from "~/components/ui/Icon"
import { RenderSection } from "@decocms/blocks/hooks"
import type { BreadcrumbList } from "@decocms/apps-commerce/types"
import { buildSeoHeadPayload } from "~/sections/SEO/CustomSEO"

import { type Section, type SectionProps as SectionProps } from "~/types/deco"
type Sections = Section[]
/**
 * @title Página
 */
interface Page {
    /**
     * @title Nome
     */
    label: string
    /**
     * @title Complemento
     * @format textarea
     */
    complement?: string
    /**
     * @title URL
     */
    url?: string
    /**
     * @title Seções
     */
    sections: Sections
}
/**
 * @title Grupo
 */
interface Group {
    /**
     * @title Nome
     */
    label: string
    /**
     * @title Páginas
     */
    pages: Page[]
}
interface Props {
    /**
     * @title Grupos
     */
    groups: Group[]
}
export default function Institutional(
    { menu, content, isMobileDevice }: SectionProps<typeof loader>,
) {
    const breadcrumb: BreadcrumbList['itemListElement'] = [
        {
            '@type': 'ListItem' as const,
            item: '/institucionais',
            name: 'Central de atendimento',
            position: 0,
        },
        {
            '@type': 'ListItem' as const,
            item: content?.url ?? '',
            name: content?.label ?? '',
            position: 1,
        },
    ]
    if (!content) {
        return (
            <div className='w-full max-w-[1810px] mx-auto'>
                <div className='mx-3 my-4 overflow-x-auto scrollbar-none flex items-center gap-2'>
                    <Breadcrumb itemListElement={breadcrumb} />
                </div>

                <h1 className='mt-8 mb-6 mx-3 text-salmon text-2xl font-black uppercase text-center'>
                    Central de Atendimento
                </h1>

                <div className='pl-[15px] pt-4 pb-12 grid justify-center grid-cols-[repeat(auto-fit,minmax(220px,1fr))] md:pl-0 xl:grid-cols-[repeat(5,minmax(220px,320px))]'>
                    {menu?.map((group, gi) => <Group key={group.label ?? gi} label={group.label} pages={group.pages} />)}
                </div>
            </div>
        )
    }
    return (
        <div className='lg:max-w-[1810px] lg:mx-auto'>
            <div className='mx-3 my-4 overflow-x-auto scrollbar-none flex items-center gap-2'>
                <Breadcrumb itemListElement={breadcrumb} />
            </div>

            <div className='grid lg:grid-cols-[17rem_1fr] lg:gap-7'>
                {isMobileDevice
                    ? (
                        <div className='drawer'>
                            <label
                                className='w-12 h-12 bg-[#b3b3b3] text-white flex justify-center items-center fixed top-48 left-0 -translate-y-1/2 z-10'
                                htmlFor='institutional-drawer'
                            >
                                <Icon id='Menu' size={24} role='img' aria-label='Abrir menu' />
                            </label>

                            <input
                                id='institutional-drawer'
                                className='drawer-toggle'
                                type='checkbox'
                                hidden
                                aria-hidden
                            />

                            <aside className='drawer-side z-50'>
                                <div className='w-full h-full flex items-center'>
                                    <div className='w-3/5 max-w-md h-full border-r-2 border-r-[#b3b3b3] overflow-y-auto bg-white'>
                                        {menu.map((group) => (
                                            <Group label={group.label} pages={group.pages} />
                                        ))}
                                    </div>

                                    <label
                                        className='w-12 h-12 bg-[#b3b3b3] text-white flex justify-center items-center shrink-0'
                                        htmlFor='institutional-drawer'
                                    >
                                        <Icon id='Menu' size={24} role='img' aria-label='Fechar menu' />
                                    </label>
                                </div>
                            </aside>
                        </div>
                    )
                    : (
                        <aside className='pb-10 border-r border-[#e6e6e6]'>
                            {menu?.map((group, gi) => <Group key={group.label ?? gi} label={group.label} pages={group.pages} />)}
                        </aside>
                    )}

                <main className='px-3 mb-28'>
                    {content.sections.map((section, i) => (
                        <RenderSection key={i} section={section as never} />
                    ))}
                </main>
            </div>
        </div>
    )
}
export const loader = ({ groups, isMobile }: Props & { isMobile?: boolean }, request: Request) => {
    const url = new URL(request.url)
    const content = groups?.flatMap(({ pages }) => pages).find((page) =>
        page.url?.toLowerCase() === url.pathname?.toLowerCase()
    )
    // Original code threw `redirect()` to /institucionais when content was
    // missing, but Fresh-era throw doesn't apply in TanStack section loaders.
    // Component handles `!content` by rendering only the navigation menu.
    const menu = groups.map(({ label, pages }) => ({
        label,
        pages: pages.map(({ sections: _sections, ...rest }) => rest),
    }))
    const isMobileDevice = isMobile ?? false

    // Surface the nested CustomSEO section's payload to the route head() so
    // each institutional sub-page gets its own <title> and <meta> instead of
    // the generic "Institucionais | Miess" coming from the page config name.
    let seoPayload: ReturnType<typeof buildSeoHeadPayload> | undefined
    if (content) {
        const seoSection = content.sections.find(
            (s) => (s as any).__resolveType === 'site/sections/SEO/CustomSEO.tsx'
        )
        if (seoSection) {
            const { __resolveType: _rt, ...seoProps } = seoSection as any
            const storeURL = `${url.protocol}//${url.host}`
            seoPayload = buildSeoHeadPayload({
                ...seoProps,
                dynamicCanonical: `${storeURL}${url.pathname.toLowerCase()}`,
            })
        }
    }

    return {
        menu,
        content,
        isMobileDevice,
        ...(seoPayload ?? {}),
    }
}
